import { useState, useCallback, useEffect, useRef } from 'react';
import { useProjectSync } from '@/hooks/useProjectSync';
import { useProjectRefresh } from '@/hooks/useProjectRefresh';
import { useFileStatus } from '../context/FileStatusContext';
import type { GetProjectQueryData, ProjectFileType } from '@/generated/graphql';
import type { ProjectType as SyncProjectType } from '@/hooks/useProjectSync';
import type { FilesTabRef } from '../tabs/FileExplorerTab';
import { useProjectVersionData } from './useProjectVersionData';
import { castFileStatuses } from '@/common/utils/statusCastingUtils';
import { useProjectChangeScan } from './useProjectChangeScan';

type ProjectDetails = GetProjectQueryData['project'];

interface UseProjectCommitProps {
  project: ProjectDetails | null;
  projectDirHandle: string | FileSystemDirectoryHandle | null;
  latestVersionFiles: ProjectFileType[];
  refetchProject: () => void;
  onProjectUpdated: () => void;
  filesWithSyncRef?: React.RefObject<FilesTabRef | null>;
  backgroundScan?: boolean;
}

/**
 * Hook to manage project commit operations and status refresh
 * Encapsulates commit dialog state, commit logic, and status refresh
 */
export function useProjectCommit({
  project,
  projectDirHandle,
  latestVersionFiles,
  refetchProject,
  onProjectUpdated,
  filesWithSyncRef,
  backgroundScan = false,
}: UseProjectCommitProps) {
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [openingScan, setOpeningScan] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string>();
  const [initialCommitMessage, setInitialCommitMessage] = useState('');
  const commitLock = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const { fileStatuses, updateFileStatuses } = useFileStatus();
  const changeScan = useProjectChangeScan({
    projectId: project?._id,
    directory: projectDirHandle,
    background: backgroundScan,
    paused: isCommitting || commitDialogOpen,
  });
  const { available: scanAvailable, scan, scanForTransfer } = changeScan;
  useEffect(() => {
    if (changeScan.snapshot) updateFileStatuses(changeScan.snapshot.statuses);
  }, [changeScan.snapshot, updateFileStatuses]);

  // Get latest version data for refresh
  const { refetchLatestVersion: refetchLatestVersionData } =
    useProjectVersionData(project);

  const { syncStatus, commitChanges, checkStatus } = useProjectSync(
    (project as SyncProjectType | null) ?? null,
    projectDirHandle,
    latestVersionFiles,
    async () => {
      refetchProject();
      onProjectUpdated();
    }
  );

  // Use shared refresh hook
  const { refreshAll, isRefreshing } = useProjectRefresh({
    refetchLatestVersionData: async () => {
      await refetchLatestVersionData();
    },
    filesWithSyncRef,
    checkStatus: async () => {
      if (changeScan.available)
        return (await changeScan.scan())?.statuses ?? null;
      const result = await checkStatus();
      return result ? castFileStatuses(result) : null;
    },
  });

  const handleRefreshStatus = useCallback(async () => {
    if (!projectDirHandle) return;
    try {
      const statuses = await refreshAll();
      if (statuses) {
        updateFileStatuses(castFileStatuses(statuses));
      }
    } catch (error) {
      console.error('Error refreshing status:', error);
    }
  }, [projectDirHandle, refreshAll, updateFileStatuses]);

  const handleCommit = useCallback(
    async (commitMessage: string) => {
      if (commitLock.current) return;
      commitLock.current = true;
      try {
        setIsCommitting(true);
        setCommitError(undefined);
        // The queued job revalidates even if navigation unmounts this page.
        const pending = commitChanges(
          commitMessage,
          scanAvailable ? scanForTransfer : undefined
        );
        setCommitDialogOpen(false);
        const result = await pending;
        if (!result.success)
          throw new Error(result.error || 'Could not create version.');
        if (mounted.current) {
          if (scanAvailable) await scan();
          else await handleRefreshStatus();
        }
      } catch (error) {
        // Failures are surfaced globally; never reopen a dialog after navigation.
        // The queue/useProjectSync already reports normal transfer failures.
        console.error('Background commit failed:', error);
      } finally {
        commitLock.current = false;
        if (mounted.current) setIsCommitting(false);
      }
    },
    [commitChanges, handleRefreshStatus, scanAvailable, scan, scanForTransfer]
  );

  const handleOpenCommitDialog = useCallback(async () => {
    setCommitDialogOpen(true);
    setCommitError(undefined);
    setOpeningScan(true);
    try {
      if (scanAvailable) await scan();
      else updateFileStatuses(castFileStatuses(await checkStatus()));
    } finally {
      setOpeningScan(false);
    }
  }, [checkStatus, updateFileStatuses, scanAvailable, scan]);

  const handleCommitFromSavePrompt = useCallback(
    async (commitMessage: string) => {
      setInitialCommitMessage(commitMessage);
      await handleCommit(commitMessage);
    },
    [handleCommit]
  );

  return {
    initialCommitMessage,
    commitDialogOpen,
    setCommitDialogOpen,
    fileStatuses: changeScan.available
      ? (changeScan.snapshot?.statuses ?? [])
      : fileStatuses,
    syncStatus,
    isRefreshing,
    isCommitting,
    isScanning: openingScan || changeScan.scanning,
    commitError:
      commitError ||
      changeScan.error ||
      (!changeScan.available ? syncStatus.error : undefined),
    changeScan,
    handleRefreshStatus,
    handleCommit,
    handleOpenCommitDialog,
    handleCommitFromSavePrompt,
  };
}
